import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type ChatInputImageAttachment } from '@apps/web/lib/chat_attachment_types';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { type Mode } from '@libraries/typescript/api_client';

// The v3 chat surface reuses `ChatInputImageAttachment` for every
// coordinator-attached upload so the composer's file-card renderer
// works unchanged for CSVs and PDFs alongside PNGs. Keep this type
// aligned with the endpoint response in
// `services/api/routes/organizations/phoebe_agent_runs/user_uploads.py`.
export type UploadedRunAttachment = ChatInputImageAttachment & {
    readonly uploadId: string | null;
};

// The endpoint enforces this too; the composer surfaces a per-file
// error instead of quietly truncating so a coordinator learns why the
// upload failed.
const MAX_UPLOAD_BYTES: number = 10 * 1024 * 1024;
const MAX_ATTACHMENTS_PER_MESSAGE = 10;

// Accepted at the browser boundary. The server still classifies bytes for
// direct callers. Keep this list aligned with the file-picker allowlist.
const ACCEPTED_UPLOAD_MIME_TYPES = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'text/csv',
    'text/plain',
    'text/markdown',
    'text/tab-separated-values',
    'application/json',
    'application/pdf',
] as const;
const ACCEPTED_MIME_TYPES: string = ACCEPTED_UPLOAD_MIME_TYPES.join(',');

function isAcceptedUploadMimeType(file: File): boolean {
    return (
        file.type.length > 0 &&
        ACCEPTED_UPLOAD_MIME_TYPES.some(
            (mimeType) => mimeType === file.type.toLowerCase(),
        )
    );
}

type UploadResponse = {
    readonly upload_id: string;
    readonly filename: string;
    readonly content_type: string;
    readonly size_bytes: number;
    readonly workspace_filepath: string;
    readonly content_sha256: string;
};

export type UseRunUploadAttachmentsResult = {
    readonly acceptedMimeTypes: string;
    readonly attachments: ReadonlyArray<UploadedRunAttachment>;
    readonly hasReadyAttachment: boolean;
    readonly hasUploadInFlight: boolean;
    readonly readyUploadIds: ReadonlyArray<string>;
    readonly errorMessage: string | null;
    readonly clearErrorMessage: () => void;
    readonly onFilesSelected: (files: Array<File>) => void;
    readonly removeAttachment: (attachmentId: string) => void;
    readonly clearAttachments: () => void;
    readonly clearAttachmentsByUploadIds: (
        uploadIds: ReadonlyArray<string>,
    ) => void;
};

export function useRunUploadAttachments(options: {
    readonly organizationId: string | null;
    readonly runId: string | null;
    readonly mode: Mode;
    readonly enabled: boolean;
}): UseRunUploadAttachmentsResult {
    const { organizationId, runId, mode, enabled } = options;
    const [attachments, setAttachments] = useState<
        Array<UploadedRunAttachment>
    >([]);
    const attachmentsRef = useRef<Array<UploadedRunAttachment>>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const clearAttachments = useCallback(() => {
        for (const attachment of attachmentsRef.current) {
            if (attachment.previewUrl) {
                URL.revokeObjectURL(attachment.previewUrl);
            }
        }
        attachmentsRef.current = [];
        setAttachments([]);
        setErrorMessage(null);
    }, []);

    // Uploads staged for one run must not follow the user into another
    // conversation: the server scopes upload rows to
    // `(organization_id, phoebe_agent_run_id)` and would 404 on send.
    // Reset whenever the effective run identity changes.
    useEffect(() => {
        clearAttachments();
    }, [clearAttachments, organizationId, runId, mode]);

    // Drops only the attachments whose ids were actually sent, so an
    // upload still in-flight when the user hits send stays in the
    // composer and can be included in the next message.
    const clearAttachmentsByUploadIds = useCallback(
        (uploadIds: ReadonlyArray<string>) => {
            if (uploadIds.length === 0) {
                return;
            }
            const uploadIdSet = new Set(uploadIds);
            const kept: Array<UploadedRunAttachment> = [];
            for (const attachment of attachmentsRef.current) {
                if (
                    attachment.uploadId !== null &&
                    uploadIdSet.has(attachment.uploadId)
                ) {
                    if (attachment.previewUrl) {
                        URL.revokeObjectURL(attachment.previewUrl);
                    }
                    continue;
                }
                kept.push(attachment);
            }
            attachmentsRef.current = kept;
            setAttachments([...kept]);
        },
        [],
    );

    const clearErrorMessage = useCallback(() => {
        setErrorMessage(null);
    }, []);

    const removeAttachment = useCallback((attachmentId: string) => {
        const removed = attachmentsRef.current.find(
            (attachment) => attachment.id === attachmentId,
        );
        if (removed?.previewUrl) {
            URL.revokeObjectURL(removed.previewUrl);
        }
        attachmentsRef.current = attachmentsRef.current.filter(
            (attachment) => attachment.id !== attachmentId,
        );
        setAttachments([...attachmentsRef.current]);
    }, []);

    const onFilesSelected = useCallback(
        (files: Array<File>) => {
            if (!enabled || files.length === 0) {
                return;
            }
            if (!organizationId || !runId) {
                setErrorMessage(
                    'Wait for the chat to open before attaching files.',
                );
                return;
            }
            setErrorMessage(null);
            const uploadRunId = runId;
            const uploadOrgId = organizationId;
            const oversized = files.filter(
                (file) => file.size > MAX_UPLOAD_BYTES,
            );
            const unsupported = files.filter(
                (file) => !isAcceptedUploadMimeType(file),
            );
            const filesToUpload = files.filter(
                (file) =>
                    file.size <= MAX_UPLOAD_BYTES &&
                    isAcceptedUploadMimeType(file),
            );
            const availableAttachmentSlots =
                MAX_ATTACHMENTS_PER_MESSAGE - attachmentsRef.current.length;
            if (filesToUpload.length > availableAttachmentSlots) {
                setErrorMessage(
                    `Attach up to ${String(MAX_ATTACHMENTS_PER_MESSAGE)} files per message. Remove an attachment before adding more.`,
                );
                return;
            }
            if (oversized.length > 0) {
                setErrorMessage(
                    `Attachments are limited to ${
                        MAX_UPLOAD_BYTES / (1024 * 1024)
                    } MB.`,
                );
            } else if (unsupported.length > 0) {
                setErrorMessage(
                    'Unsupported file type. Choose an image, text file, CSV, JSON, or PDF.',
                );
            }
            if (filesToUpload.length === 0) {
                return;
            }
            const nextAttachments: Array<UploadedRunAttachment> =
                filesToUpload.map((file) => ({
                    id: crypto.randomUUID(),
                    filename: file.name || null,
                    mimeType: file.type || 'application/octet-stream',
                    sizeBytes: file.size,
                    previewUrl: file.type.startsWith('image/')
                        ? URL.createObjectURL(file)
                        : null,
                    status: 'uploading',
                    uploadId: null,
                }));
            attachmentsRef.current = [
                ...attachmentsRef.current,
                ...nextAttachments,
            ];
            setAttachments([...attachmentsRef.current]);

            void (async () => {
                await Promise.all(
                    nextAttachments.map(async (attachment, index) => {
                        const file = filesToUpload[index];
                        if (!file) {
                            return;
                        }
                        try {
                            const uploaded = await postRunUpload({
                                organizationId: uploadOrgId,
                                runId: uploadRunId,
                                file,
                                mode,
                                // The client-generated id is the retry
                                // dedup handle on the server: a network
                                // flap retry sends the same operation
                                // id and lands on the existing row.
                                clientOperationId: attachment.id,
                            });
                            attachmentsRef.current = attachmentsRef.current.map(
                                (existing) =>
                                    existing.id === attachment.id
                                        ? {
                                              ...existing,
                                              filename: uploaded.filename,
                                              mimeType: uploaded.content_type,
                                              sizeBytes: uploaded.size_bytes,
                                              status: 'ready',
                                              uploadId: uploaded.upload_id,
                                          }
                                        : existing,
                            );
                            setAttachments([...attachmentsRef.current]);
                        } catch (error) {
                            attachmentsRef.current = attachmentsRef.current.map(
                                (existing) =>
                                    existing.id === attachment.id
                                        ? {
                                              ...existing,
                                              status: 'failed',
                                              errorMessage:
                                                  error instanceof Error
                                                      ? error.message
                                                      : 'Upload failed.',
                                          }
                                        : existing,
                            );
                            setAttachments([...attachmentsRef.current]);
                        }
                    }),
                );
            })();
        },
        [enabled, mode, organizationId, runId],
    );

    const readyUploadIds = useMemo(
        () =>
            attachments
                .filter(
                    (attachment): attachment is UploadedRunAttachment =>
                        attachment.status === 'ready' &&
                        attachment.uploadId !== null,
                )
                .map((attachment) => attachment.uploadId as string),
        [attachments],
    );
    const hasUploadInFlight = useMemo(
        () =>
            attachments.some((attachment) => attachment.status === 'uploading'),
        [attachments],
    );

    return {
        acceptedMimeTypes: ACCEPTED_MIME_TYPES,
        attachments,
        hasReadyAttachment: readyUploadIds.length > 0,
        hasUploadInFlight,
        readyUploadIds,
        errorMessage,
        clearErrorMessage,
        onFilesSelected,
        removeAttachment,
        clearAttachments,
        clearAttachmentsByUploadIds,
    };
}

async function postRunUpload(params: {
    readonly organizationId: string;
    readonly runId: string;
    readonly file: File;
    readonly mode: Mode;
    readonly clientOperationId: string;
}): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('client_operation_id', params.clientOperationId);
    const response = await fetch(
        `${getRuntimeConfig().apiUrl}/organizations/${
            params.organizationId
        }/phoebe_agent_runs/${params.runId}/user-uploads`,
        {
            method: 'POST',
            credentials: 'include',
            headers: { 'X-Mode': params.mode },
            body: formData,
        },
    );
    const body = (await response.json().catch(() => null)) as
        | (UploadResponse & { readonly detail?: string })
        | { readonly detail?: string }
        | null;
    if (!response.ok) {
        throw new Error(
            (body && 'detail' in body ? body.detail : null) ??
                `Upload failed (${response.status}).`,
        );
    }
    if (!body || !('upload_id' in body)) {
        throw new Error('Upload response missing upload_id.');
    }
    return body;
}
